/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 04/05/2022
 */

import React, { Fragment } from 'react';
import { Cell, ListCell, Row, ValueCell } from '@/wpt-lib/pdf/common/Table';
import { useLabel, useLabels } from '@/wpt-lib/pdf/useData';
import { label } from '@/wpt-lib/utils/label';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';

const ResearchAndScholarship = ({
    keyPerformanceIndicatorsResearch,
    typeOfWork,
    existingResearchProjects,
    newResearchProjects,
    perceivedImpactArray,
    nonTraditionalResearchOutputs,
    principlePlannedNumberOfPhDCandidates,
    principalPlannedNumberOfHonoursCandidates,
    principlePlannedNumberOfMasterResearchCandidates,
    CoSupervisoryPlannedNumberOfPhDCandidates,
    CoSupervisoryPlannedNumberOfHonoursCandidates,
    CoSupervisoryPlannedNumberOfMasterResearchCandidates,
    assistantPlannedNumberOfPhDCandidates,
    assistantPlannedNumberOfHonoursCandidates,
    assistantPlannedNumberOfMasterResearchCandidates,
    targetIncome,
    targetDate,
    overviewOfOpportunity,
}: Partial<{
    keyPerformanceIndicatorsResearch: string;
    typeOfWork: {
        researchProjects: boolean;
        supervisionAndCompletion: boolean;
        businessDevelopmentOpportunities: boolean;
    };
    existingResearchProjects: [
        Partial<{
            researchMaster: Partial<{
                researchMasterAutoCompleteValue: string;
                projectEndDate: string;
                fundingOrg: string;
                incomeCategory: string;
                existingLeadResearcher: string;
            }>;
            targetResearchSpendForThisYear: number;
            currentStage: string;
        }>,
    ];
    newResearchProjects: [
        Partial<{
            newProjectName: string;
            targetResearchIncomeThisYear: string;
            targetFundingOrg: string;
            targetIncomeCategory: { [cat: string]: boolean };
            newResearchProjectLeadResearcher: string;
        }>,
    ];
    perceivedImpactArray: [
        Partial<{
            perceivedImpact: string;
        }>,
    ];
    nonTraditionalResearchOutputs: [
        Partial<{
            researchOutput: string;
        }>,
    ];
    principlePlannedNumberOfPhDCandidates: number;
    principalPlannedNumberOfHonoursCandidates: number;
    principlePlannedNumberOfMasterResearchCandidates: number;
    CoSupervisoryPlannedNumberOfPhDCandidates: number;
    CoSupervisoryPlannedNumberOfHonoursCandidates: number;
    CoSupervisoryPlannedNumberOfMasterResearchCandidates: number;
    assistantPlannedNumberOfPhDCandidates: number;
    assistantPlannedNumberOfHonoursCandidates: number;
    assistantPlannedNumberOfMasterResearchCandidates: number;
    targetIncome: number;
    targetDate: string;
    overviewOfOpportunity: string;
}>) => {
    const cats = useLabels('targetIncomeCategory');

    return (
        <>
            {keyPerformanceIndicatorsResearch && (
                <Row>
                    <ValueCell label="Key Performance Indicators (KPIs)" value={keyPerformanceIndicatorsResearch} />
                </Row>
            )}
            {typeOfWork?.researchProjects && (
                <>
                    <Row>
                        <SubHeading level={2} text="Research Projects" />
                    </Row>

                    {Array.isArray(existingResearchProjects) && existingResearchProjects.length > 0 && (
                        <Row>
                            <SubHeading level={3} text="Existing Research Projects" />
                        </Row>
                    )}
                    {existingResearchProjects?.map((existingResearchProject, i: number) => (
                        <Fragment key={i}>
                            <Row>
                                <ValueCell
                                    label="Project Name"
                                    value={existingResearchProject.researchMaster?.researchMasterAutoCompleteValue}
                                />
                            </Row>
                            <Row>
                                <ValueCell
                                    label="Project End date"
                                    value={existingResearchProject.researchMaster?.projectEndDate}
                                />
                                <ValueCell
                                    label="Funding Organisation"
                                    value={existingResearchProject.researchMaster?.fundingOrg}
                                />
                                <ValueCell
                                    label="Income Category"
                                    value={existingResearchProject.researchMaster?.incomeCategory}
                                />
                            </Row>
                            <Row>
                                <ValueCell
                                    label="Lead Researcher"
                                    value={existingResearchProject.researchMaster?.existingLeadResearcher}
                                />
                                <ValueCell
                                    label="Target Research Spend For This Year"
                                    value={existingResearchProject.targetResearchSpendForThisYear}
                                />
                                <ValueCell label="Current Stage" value={existingResearchProject.currentStage} />
                            </Row>
                            <Row>
                                <Cell />
                            </Row>
                        </Fragment>
                    ))}
                    {Array.isArray(newResearchProjects) && newResearchProjects.length > 0 && (
                        <Row>
                            <SubHeading level={3} text="New Research Projects" />
                        </Row>
                    )}
                    {newResearchProjects?.map((newResearchProject, i: number) => (
                        <Fragment key={i}>
                            <Row>
                                <ValueCell label="Project Name" value={newResearchProject.newProjectName} />
                            </Row>
                            <Row>
                                <ValueCell
                                    label="Target Research Income For The Year"
                                    value={newResearchProject.targetResearchIncomeThisYear}
                                />
                                <ValueCell label="Funding Organisation" value={newResearchProject.targetFundingOrg} />
                                <ValueCell
                                    label="Lead Researcher"
                                    value={
                                        newResearchProject.newResearchProjectLeadResearcher
                                            ? useLabel(
                                                  'yes-no-unsure',
                                                  newResearchProject.newResearchProjectLeadResearcher ?? '',
                                              )
                                            : 'n/a'
                                    }
                                />
                            </Row>
                            <Row>
                                <ListCell
                                    label="Target Income Category"
                                    values={
                                        newResearchProject.targetIncomeCategory
                                            ? Object.entries(newResearchProject.targetIncomeCategory)
                                                  .filter(([, y]) => y)
                                                  .map(([c]) => label(cats, c))
                                            : ['n/a']
                                    }
                                />
                            </Row>
                            <Row>
                                <Cell />
                            </Row>
                        </Fragment>
                    ))}
                    {(perceivedImpactArray || nonTraditionalResearchOutputs) && (
                        <Row>
                            <SubHeading level={2} text="Research Dissemination and Impact" />
                        </Row>
                    )}
                    {perceivedImpactArray && (
                        <Row>
                            <SubHeading level={3} text="Perceived Impacts" />
                        </Row>
                    )}
                    {perceivedImpactArray?.map((impact, i: number) => (
                        <Row key={i}>
                            <ValueCell label={`Perceived Impact - ${i + 1}`} value={impact.perceivedImpact} />
                        </Row>
                    ))}
                    {nonTraditionalResearchOutputs && (
                        <Row>
                            <SubHeading level={3} text="Targeted publications / non-Traditional Research Outputs" />
                        </Row>
                    )}
                    {nonTraditionalResearchOutputs?.map((output, i: number) => (
                        <Row key={i}>
                            <ValueCell label={`Research Output - ${i + 1}`} value={output.researchOutput} />
                        </Row>
                    ))}
                </>
            )}
            {typeOfWork?.supervisionAndCompletion && (
                <>
                    <Row>
                        <SubHeading level={2} text="Supervision and Completion" />
                    </Row>
                    <Row>
                        <SubHeading level={3} text="Principal Supervisory Role" />
                    </Row>
                    <Row>
                        <ValueCell
                            label="Planned Number Of PhD Candidates"
                            value={principlePlannedNumberOfPhDCandidates ?? 'n/a'}
                        />

                        <ValueCell
                            label="Planned Number Of Honours Candidates"
                            value={principalPlannedNumberOfHonoursCandidates ?? 'n/a'}
                        />

                        <ValueCell
                            label="Planned Number Of Master (Research) Candidates"
                            value={principlePlannedNumberOfMasterResearchCandidates ?? 'n/a'}
                        />
                    </Row>
                    <Row>
                        <SubHeading level={3} text="Co-Supervisory Role" />
                    </Row>
                    <Row>
                        <ValueCell
                            label="Planned Number Of PhD Candidates"
                            value={CoSupervisoryPlannedNumberOfPhDCandidates ?? 'n/a'}
                        />
                        <ValueCell
                            label="Planned Number Of Honours Candidates"
                            value={CoSupervisoryPlannedNumberOfHonoursCandidates ?? 'n/a'}
                        />
                        <ValueCell
                            label="Planned Number Of Master (Research) Candidates"
                            value={CoSupervisoryPlannedNumberOfMasterResearchCandidates ?? 'n/a'}
                        />
                    </Row>
                    <Row>
                        <SubHeading level={3} text="Assistant Supervisory Role" />
                    </Row>
                    <Row>
                        <ValueCell
                            label="Planned Number Of PhD Candidates"
                            value={assistantPlannedNumberOfPhDCandidates ?? 'n/a'}
                        />
                        <ValueCell
                            label="Planned Number Of Honours Candidates"
                            value={assistantPlannedNumberOfHonoursCandidates ?? 'n/a'}
                        />

                        <ValueCell
                            label="Planned Number Of Master (Research) Candidates"
                            value={assistantPlannedNumberOfMasterResearchCandidates ?? 'n/a'}
                        />
                    </Row>
                </>
            )}
            {typeOfWork?.businessDevelopmentOpportunities && (
                <>
                    <Row>
                        <SubHeading level={2} text="Business Development Opportunities" />
                    </Row>
                    <Row>
                        <ValueCell label="Target Income" value={targetIncome ?? 'n/a'} />
                        <ValueCell label="Target Date" value={targetDate ?? 'n/a'} />
                    </Row>
                    <Row>
                        <ValueCell label="Overview of Opportunity" value={overviewOfOpportunity ?? 'n/a'} />
                    </Row>
                </>
            )}
        </>
    );
};

export default ResearchAndScholarship;
